import {
  describeAuditEvent,
  fetchAllAuditTrailMatching,
  formatEventType,
  useAuditTrailSearch,
  useUserAuditSummary,
} from '@/domains/settings';
import { Pagination } from '@/shared/components/Pagination';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import type { AuditTrailItem } from '@skewb-works/contracts';
import {
  Award,
  Calendar,
  Download,
  FileCheck,
  FileText,
  History,
  Loader2,
  Shield,
  User as UserIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import type { User } from '../types/user';
import { downloadLogCsv } from '../utils/exportLogCsv';
import { ActivityTimelineSkeleton } from './ActivityTimelineSkeleton';

const PAGE_SIZE = 25;

/** Visual treatment for the timeline node, keyed off the audit event type. */
function eventTone(eventType: AuditTrailItem['event_type']): LogEntry['type'] {
  switch (eventType) {
    case 'creation':
    case 'assignment':
    case 'completion':
      return 'success';
    case 'deletion':
    case 'authority_override':
      return 'warning';
    default:
      return 'info';
  }
}

/** Maps a live audit-trail row to the timeline's view-model. */
function toLogEntry(row: AuditTrailItem): LogEntry {
  const created = new Date(row.created_at);
  return {
    id: row.id,
    verifier: row.username || 'System',
    date: created.toLocaleDateString('en-GB'),
    time: created.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    notes: describeAuditEvent(row),
    action: formatEventType(row.event_type),
    type: eventTone(row.event_type),
  };
}

interface LogEntry {
  id: number;
  verifier: string;
  date: string;
  time: string;
  notes: string;
  action: string;
  type: 'success' | 'warning' | 'info';
}

interface LogSectionProps {
  user: User;
  userId?: string;
  /**
   * This user's line manager. Fills the exported log's Manager column, which was
   * shipping empty (SC-6526).
   */
  lineManager?: string | null;
  /** Current 1-indexed page. Controlled by the parent so it can persist to the URL. */
  page: number;
  /** Reports the requested page upward. */
  onPageChange: (page: number) => void;
}

export function LogSection({ user, userId, lineManager, page, onPageChange }: LogSectionProps) {
  // The audit trail keys actions by the numeric user id of the actor.
  const actorId = Number(userId || user.id);
  const [isExporting, setIsExporting] = useState(false);

  const {
    data: searchData,
    isLoading,
    isPlaceholderData,
  } = useAuditTrailSearch({
    user_id: actorId,
    limit: PAGE_SIZE,
    skip: (page - 1) * PAGE_SIZE,
  });
  const { data: summary } = useUserAuditSummary(actorId);

  const logs: LogEntry[] = useMemo(() => (searchData?.data ?? []).map(toLogEntry), [searchData]);

  // Summary cards reflect the user's whole log (server aggregates), not the page.
  const totalCount = summary?.count ?? 0;
  const lastActivity = summary?.lastActivity
    ? new Date(summary.lastActivity).toLocaleDateString('en-GB')
    : '—';
  const thisMonthCount = summary?.actionsThisMonth ?? 0;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const rows = await fetchAllAuditTrailMatching({ user_id: actorId });
      if (rows.length === 0) return;
      // Every exported row is filtered to this user as the actor, so the manager
      // for the whole file is this user's line manager. Blank (not 'N/A') when
      // unset — spreadsheet consumers filter on empty cells.
      const manager = lineManager ?? '';
      downloadLogCsv(
        rows.map((row) => {
          const entry = toLogEntry(row);
          return {
            date: entry.date,
            time: entry.time,
            action: entry.action,
            notes: entry.notes,
            verifier: entry.verifier,
            manager,
          };
        }),
        `${user.name.replace(/\s+/g, '_').toLowerCase()}_activity_log.csv`,
      );
    } finally {
      setIsExporting(false);
    }
  };

  const getActionBadgeColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border-green-300 dark:border-green-700';
      case 'warning':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-amber-300 dark:border-amber-700';
      case 'info':
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200 border-gray-300 dark:border-gray-700';
    }
  };

  return (
    <div className="max-w-full space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950/50 dark:to-gray-900/50 border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-900 dark:text-gray-100 opacity-70 mb-1">
                Total Log Entries
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalCount}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-gray-200/50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 flex items-center justify-center">
              <History className="w-6 h-6" />
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950/50 dark:to-gray-900/50 border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-900 dark:text-gray-100 opacity-70 mb-1">
                Last Activity
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{lastActivity}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-gray-200/50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 flex items-center justify-center">
              <Calendar className="w-6 h-6" />
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950/50 dark:to-gray-900/50 border border-border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-900 dark:text-gray-100 opacity-70 mb-1">
                Actions This Month
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {thisMonthCount}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-gray-200/50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 flex items-center justify-center">
              <FileCheck className="w-6 h-6" />
            </div>
          </div>
        </Card>
      </div>

      {/* Activity Timeline */}
      <Card className="border-border">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <History className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Activity Timeline</h3>
                <p className="text-xs text-muted-foreground">
                  Complete history of user actions and changes
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-border"
              disabled={totalCount === 0 || isExporting}
              onClick={handleExport}
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export Log
            </Button>
          </div>
        </div>
        <div className="p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Loading activity...</span>
            </div>
          )}

          {/* Paging with keepPreviousData keeps the old page visible; swap it
              for a skeleton so the swap reads as a load, not stale data. */}
          {!isLoading && isPlaceholderData && (
            <ActivityTimelineSkeleton rows={Math.min(logs.length || PAGE_SIZE, PAGE_SIZE)} />
          )}

          {!isLoading && !isPlaceholderData && logs.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <History className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">No activity recorded yet</p>
              </div>
            </div>
          )}

          {!isLoading && !isPlaceholderData && logs.length > 0 && (
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
              <div className="space-y-6">
                {logs.map((log: LogEntry) => (
                  <div key={log.id} className="relative flex gap-6">
                    <div className="relative z-10 flex-shrink-0">
                      <div
                        className={`w-12 h-12 rounded-full border-4 border-background flex items-center justify-center ${
                          log.type === 'success'
                            ? 'bg-green-500 dark:bg-green-600'
                            : log.type === 'warning'
                              ? 'bg-amber-500 dark:bg-amber-600'
                              : 'bg-gray-400 dark:bg-gray-500'
                        }`}
                      >
                        {log.type === 'success' && <Award className="w-5 h-5 text-white" />}
                        {log.type === 'warning' && <Shield className="w-5 h-5 text-white" />}
                        {log.type === 'info' && <FileText className="w-5 h-5 text-white" />}
                      </div>
                    </div>
                    <div className="flex-1 pb-4">
                      <Card>
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className={getActionBadgeColor(log.type)}>
                                  {log.action}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {log.date} at {log.time}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-foreground">{log.notes}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                            <div className="flex items-center gap-1.5">
                              <UserIcon className="w-3.5 h-3.5" />
                              <span>
                                By:{' '}
                                <span className="font-medium text-foreground">{log.verifier}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {totalCount > 0 && (
          <div className="border-t border-border px-4 py-3">
            <Pagination
              page={page}
              totalItems={totalCount}
              pageSize={PAGE_SIZE}
              itemNoun="activities"
              onPageChange={onPageChange}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
